import { zipSync } from 'fflate';

type ArchiveItem = {
  public_url: string;
  filename?: string;
  media_id?: string;
};

/** Stiahnutie `Blob`-u pod daným menom cez odkaz, ktorý sa hneď zahodí. */
const saveBlob = (fileName: string, blob: Blob) => {
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = fileName;
  document.body.appendChild(link);
  link.click();
  link.remove();
  // Hneď po `click()` ešte Safari a Firefox zo súboru nemusia mať nič
  // prečítané a uvoľnená adresa by sťahovanie zrušila.
  setTimeout(() => URL.revokeObjectURL(url), 60_000);
};

/**
 * Mená súborov v archíve musia byť jedinečné - dve fotky z mobilu sa kľudne
 * volajú obe `image.jpg` a ZIP by jednu z nich pri rozbalení prepísal.
 * Druhá dostane ` (2)` pred príponou, tak ako to robí prehliadač pri
 * sťahovaní.
 */
const uniqueNames = (items: ArchiveItem[]) => {
  const used = new Set<string>();
  return items.map((item, index) => {
    const base =
      item.filename?.replace(/[\\/]/g, '_') ||
      item.media_id ||
      `priloha-${index + 1}`;
    let name = base;
    for (let n = 2; used.has(name.toLowerCase()); n += 1) {
      const dot = base.lastIndexOf('.');
      name =
        dot > 0
          ? `${base.slice(0, dot)} (${n})${base.slice(dot)}`
          : `${base} (${n})`;
    }
    used.add(name.toLowerCase());
    return name;
  });
};

/**
 * Stiahne všetky prílohy naraz.
 *
 * `window.open` v cykle - to, čo tu bolo predtým - prehliadač pustí len
 * **raz**: ďalšie okná nevznikli z kliknutia, takže ich blokovač vyskakovacích
 * okien zahodí potichu a úradník dostal len prvú prílohu (moj-presov#7).
 * Jedno kliknutie teda musí dať jeden súbor.
 *
 * Jedna príloha sa stiahne tak, ako je; viac sa zabalí do ZIP-u priamo
 * v prehliadači. Prílohy ležia na tej istej doméne (`/data` smeruje na
 * digiobcan-api), takže ich `fetch` prečíta bez CORS a `download` na odkaze
 * platí. Balí sa **bez kompresie** (`level: 0`): fotky a PDF sú už
 * skomprimované a druhé kolo by len pálilo procesor.
 */
export const downloadMediaArchive = async (
  items: ArchiveItem[],
  archiveName: string,
) => {
  const names = uniqueNames(items);
  const contents = await Promise.all(
    items.map(async (item) => {
      const response = await fetch(item.public_url);
      if (!response.ok) {
        throw new Error(`HTTP ${response.status} ${item.public_url}`);
      }
      return response.blob();
    }),
  );

  if (contents.length === 1) {
    saveBlob(names[0], contents[0]);
    return;
  }

  const files: Record<string, Uint8Array> = {};
  await Promise.all(
    contents.map(async (blob, index) => {
      files[names[index]] = new Uint8Array(await blob.arrayBuffer());
    }),
  );

  saveBlob(
    archiveName,
    new Blob([zipSync(files, { level: 0 })], { type: 'application/zip' }),
  );
};
